// BM25 Parameters
const k1 = 1.5;  // term frequency saturation parameter
const b = 0.75;  // length normalization parameter

interface Document {
  id: string;
  title: string;
  category: string;
  content: string;
}

export class BM25 {
  private documents: Document[];
  private avgDocLength: number;
  private documentFrequency: Map<string, number>;
  private termFrequency: Map<string, Map<string, number>>;

  constructor(documents: Document[]) {
    this.documents = documents;
    this.documentFrequency = new Map();
    this.termFrequency = new Map();
    this.avgDocLength = this.calculateAverageDocumentLength();
    this.preprocessDocuments();
  }

  private tokenize(text: string): string[] {
    return text.toLowerCase()
      .replace(/[^\w\s]/g, '')
      .split(/\s+/)
      .filter(term => term.length > 0);
  }

  private calculateAverageDocumentLength(): number {
    const totalLength = this.documents.reduce((sum, doc) => {
      const content = `${doc.title} ${doc.category} ${doc.content}`;
      return sum + this.tokenize(content).length;
    }, 0);
    return totalLength / this.documents.length;
  }

  private preprocessDocuments(): void {
    this.documents.forEach(doc => {
      const content = `${doc.title} ${doc.category} ${doc.content}`;
      const terms = this.tokenize(content);
      
      // Calculate term frequency for this document
      const docTerms = new Map<string, number>();
      terms.forEach(term => {
        docTerms.set(term, (docTerms.get(term) || 0) + 1);
        this.documentFrequency.set(term, (this.documentFrequency.get(term) || 0) + 1);
      });
      
      this.termFrequency.set(doc.id, docTerms);
    });
  }

  private score(query: string, doc: Document): number {
    const queryTerms = this.tokenize(query);
    const docLength = this.tokenize(`${doc.title} ${doc.category} ${doc.content}`).length;
    
    return queryTerms.reduce((score, term) => {
      const tf = this.termFrequency.get(doc.id)?.get(term) || 0;
      const df = this.documentFrequency.get(term) || 0;
      
      if (df === 0) return score;
      
      const idf = Math.log((this.documents.length - df + 0.5) / (df + 0.5) + 1);
      const numerator = tf * (k1 + 1);
      const denominator = tf + k1 * (1 - b + b * docLength / this.avgDocLength);
      
      return score + idf * numerator / denominator;
    }, 0);
  }

  search(query: string, limit: number = 10): Document[] {
    return this.documents
      .map(doc => ({
        doc,
        score: this.score(query, doc)
      }))
      .filter(result => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(result => result.doc);
  }
}